import { Formik, FormikProps } from 'formik';
import { Grid } from '@mui/material';
import { ScrollToFieldError } from '@synergy/components';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import * as ContactOverviewActions from '../overview/store/contact-detail-overview-actions';
import * as ContactPermissionActions from './store/contact-detail-permissions-actions';
import { ContactDetailPermissionsCredentials } from './components/contact-detail-permissions-credentials';
import { ContactDetailPermissionsRolesAdmin } from './components/contact-detail-permissions-roles-admin';
import { ContactDetailPermissionsRolesCustomer } from './components/contact-detail-permissions-roles-customer';
import { ContactDetailPermissionsRolesSulu } from './components/contact-detail-permissions-roles-sulu';
import { ContactDetailPermissionsRolesSupplier } from './components/contact-detail-permissions-roles-supplier';
import { ContactPermissionsFormValues } from './models/contact-permissions-form-values';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { contactDetailPermissionsValidationSchema } from './contact-detail-permissions-validation-schema';
import {
    contactPermissionsBaselineSelector,
    contactPermissionsFormDataSelector,
} from './store/contact-detail-permissions-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const ContactDetailPermissions = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const formikRef = useRef<FormikProps<ContactPermissionsFormValues>>(null);

    const dispatch = useThunkDispatch();
    const { formValues, errors, touched } = useAppSelector(contactPermissionsFormDataSelector);
    // The permissions as the server has them — see PaloDirtyComparison.
    const baseline = useAppSelector(contactPermissionsBaselineSelector);
    const permissions = useAppSelector(
        state => state.contactsAndOrganizations.contacts.contactDetail.permissions.permissions
    );
    const contact = useAppSelector(state => state.contactsAndOrganizations.contacts.contactDetail.overview.contact);
    const { id: contactIdParam } = useParams<{ id: string }>();

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(ContactPermissionActions.updateContactPermissions(values, isDirty, isValid, errs));
    });

    useEffect(() => {
        if (!permissions && contactIdParam) {
            dispatch(ContactPermissionActions.fetchContactPermissions(+contactIdParam));
            // Contact-Detail is needed to set email. fetch if missing.
            if (!contact?.id) {
                dispatch(ContactOverviewActions.fetchContactDetails(+contactIdParam));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact?.id, contactIdParam, dispatch]);

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={contactDetailPermissionsValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            <>
                <PaloDirtyComparison baseline={baseline} />
                <ScrollToFieldError />
                <Grid container={true} spacing={6}>
                    <Grid item={true} xs={12}>
                        <ContactDetailPermissionsCredentials />
                    </Grid>
                    <Grid item={true} container={true} xs={12} spacing={6}>
                        <Grid item={true} xs={12}>
                            <ContactDetailPermissionsRolesAdmin />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <ContactDetailPermissionsRolesCustomer />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <ContactDetailPermissionsRolesSupplier />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <ContactDetailPermissionsRolesSulu />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        </Formik>
    );
});
